// New / change booking (Batch 4 redesign): 1 Who and when (More details
// folded), 2 Menu and price, 3 Extras, and a Quote card with the one main
// button. Priced per dish or per guest ("Per head / Per dish" in Menu and
// price; a new booking starts from the business default in Settings); the
// quote comes from quoteSummary. Rules: lib/inventoryFlow/cateringLogic.ts.
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ChevronDown, Trash2 } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { formatMoney } from '@/lib/currency'
import { cn } from '@/lib/utils'
import { PT } from '@/lib/inventoryFlow/planStrings'
import { CBT, CT, CXT, PGT } from '@/lib/inventoryFlow/cateringStrings'
import { useAllSettings } from '@/hooks/useKitchenFlowEnabled'
import { newIdempotencyKey } from '@/lib/inventoryFlow/offlineQueue'
import { localTomorrow } from '@/lib/inventoryFlow/planLogic'
import type { CateringBooking, KitchenEdit, PaymentMethod } from '@/lib/inventoryFlow/cateringTypes'
import {
  addLine, applyGuestsOffer, bookingInputFrom, defaultPortions, depositTooBig, dishesEditable, draftDirty, draftFromBooking, draftProblem, emptyDraft,
  extrasSet, guestsChangeOffer, guestsOf, isBookingChanged, kitchenEditsOf, kitchenEditsText, matchesGuests, moreDetailsSet, parseAmount, parseCateringPricing, portionHints, pricingSwitchChange,
  quoteSummary, removeLine, setLine, showPricingSwitch, unitPriceOf, type BookingDraft, type PricingMode,
} from '@/lib/inventoryFlow/cateringLogic'
import { ExtrasPicker } from '@/components/inventory-flow/PlansTab'
import { ItemAvatar } from '@/components/inventory-flow/StatusChip'
import { QtyStepper } from '@/components/inventory-flow/QtyStepper'
import { ConfirmDialog } from './ConfirmDialog'
import { EquipmentClashNotice } from './EquipmentClashNotice'
import { returnableAsk } from '@/lib/inventoryFlow/equipmentLogic'

type Notify = (kind: 'success' | 'error', message: string) => void

const money = (n: number) => formatMoney(n)

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message.split(' — ')[0] : 'Something went wrong'
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms)
    return () => window.clearTimeout(t)
  }, [value, ms])
  return v
}

export function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={cn('block space-y-1.5', className)}>
      <span className="text-sm font-medium">{label}</span>
      {children}
    </label>
  )
}

/** ReportsShell-style segmented choice. */
function Segmented<V extends string>({ value, options, onChange, disabled, label }: {
  value: V; options: { value: V; label: string }[]; onChange: (v: V) => void; disabled?: boolean; label: string
}) {
  return (
    <div className="flex w-fit max-w-full flex-wrap gap-1 rounded-lg bg-muted/60 p-1" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} disabled={disabled} onClick={() => onChange(o.value)}
          className={cn('whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60',
            value === o.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

function Disclosure({ open, onToggle, title, children, className }: { open: boolean; onToggle: () => void; title: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <button type="button" aria-expanded={open} onClick={onToggle} className="flex w-full items-center justify-between gap-2 text-left text-sm font-medium text-muted-foreground hover:text-foreground">
        <span>{title}</span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')} />
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  )
}

export function BookingForm({ booking, notify, onDone }: { booking: CateringBooking | null; notify: Notify; onDone: (b: CateringBooking | null) => void }) {
  const qc = useQueryClient()
  const defaultPricing = parseCateringPricing(useAllSettings())
  const [start, setStart] = useState<BookingDraft>(() => (booking ? draftFromBooking(booking) : emptyDraft(localTomorrow(), defaultPricing)))
  const [d, setD] = useState<BookingDraft>(start)
  // Settings can arrive after the form opens: an untouched new booking
  // follows the business default.
  useEffect(() => {
    if (booking || draftDirty(d, start) || start.pricing_mode === defaultPricing) return
    const next = { ...start, pricing_mode: defaultPricing }
    setStart(next)
    setD(next)
  }, [booking, d, start, defaultPricing])
  const [askSwitch, setAskSwitch] = useState(false)
  // Batch 6: the save would set back numbers the kitchen changed on the plan;
  // what to show, and the save to repeat once the manager agrees.
  const [kitchenEdits, setKitchenEdits] = useState<{ edits: KitchenEdit[]; confirm: boolean } | null>(null)
  const [askDiscard, setAskDiscard] = useState(false)
  const [search, setSearch] = useState('')
  const [saveKey] = useState(newIdempotencyKey)
  const [depositKey] = useState(newIdempotencyKey)
  const [showMore, setShowMore] = useState(() => moreDetailsSet(start))
  const [showExtras, setShowExtras] = useState(() => extrasSet(start))
  // The guest count the dishes were last lined up with; changing Guests
  // offers to move the dishes that matched it (never silently). It settles
  // when the Guests box is left, so "2", "25" on the way to "250" don't count.
  const [offerFrom, setOfferFrom] = useState(start.guest_count)
  const [guestsFocused, setGuestsFocused] = useState(false)
  const [deposit, setDeposit] = useState('')
  const [depositMethod, setDepositMethod] = useState<PaymentMethod>('cash')
  // The revision this draft started from; a save on top of a newer one is
  // refused (booking_changed) and the draft stays on screen.
  const [baseRevision, setBaseRevision] = useState(booking?.revision)
  const [stale, setStale] = useState(false)
  const debouncedSearch = useDebounced(search.trim(), 250)
  const set = <K extends keyof BookingDraft>(k: K, v: BookingDraft[K]) => setD((x) => ({ ...x, [k]: v }))
  const lockDishes = booking ? !dishesEditable(booking.status) : false

  const { data: productsRes } = useQuery({
    queryKey: ['catering-dishes', debouncedSearch],
    queryFn: () => apiClient.getProducts({ search: debouncedSearch || undefined, per_page: 40, available: true }),
    enabled: !lockDishes,
  })
  const products = productsRes?.data ?? []
  // Batch 5: equipment asked for that day beyond what is owned (a warning).
  const equipmentAsk = useDebounced(JSON.stringify({ date: d.event_date, extras: returnableAsk(d.extras) }), 400)
  const { data: clashRes } = useQuery({
    queryKey: ['inventory-flow', 'catering', 'equipment-check', booking?.id ?? '', equipmentAsk],
    queryFn: () => {
      const a = JSON.parse(equipmentAsk) as { date: string; extras: { stock_item_id: string; qty: number }[] }
      return apiClient.checkCateringEquipment({ event_date: a.date, booking_id: booking?.id, extras: a.extras })
    },
    enabled: Boolean(d.event_date) && returnableAsk(d.extras) != null,
    retry: false,
  })
  const clashes = returnableAsk(d.extras) ? clashRes?.data ?? [] : []
  const quote = quoteSummary(d, { paid: booking?.totals.paid ?? 0 })
  const totals = quote.totals
  const problem = draftProblem(d)
  const hints = portionHints(d)
  const offer = lockDishes || guestsFocused ? null : guestsChangeOffer(d.lines, offerFrom, d.guest_count)
  const depositAmt = deposit.trim() === '' ? null : parseAmount(deposit)
  const badDeposit = deposit.trim() !== '' && depositAmt == null
  const bigDeposit = depositTooBig(depositAmt, totals.total)
  const blocked = Boolean(problem) || badDeposit || bigDeposit
  const perGuest = d.pricing_mode === 'per_guest'
  // Switching a confirmed booking's pricing changes what the customer agreed
  // to pay: say so before saving.
  const switchChange = pricingSwitchChange(booking, d)
  const back = () => (draftDirty(d, start) || deposit.trim() !== '' ? setAskDiscard(true) : onDone(null))

  const save = useMutation({
    mutationFn: async (vars: boolean | { confirm: boolean; replaceKitchenEdits: boolean }): Promise<{ booking: CateringBooking | null; depositFailed?: boolean }> => {
      const confirm = typeof vars === 'boolean' ? vars : vars.confirm
      const replace = typeof vars !== 'boolean' && vars.replaceKitchenEdits
      const input = {
        ...bookingInputFrom(d, confirm),
        ...(booking ? { revision: baseRevision } : {}),
        ...(booking && replace ? { replace_kitchen_edits: true } : {}),
      }
      if (booking) {
        const res = await apiClient.updateCateringBooking(booking.id, input, saveKey)
        const rev = res.data?.plan_revision
        if (rev) {
          const bits: string[] = [CT.kitchenModes[rev.mode]]
          if (rev.top_up_req_no) bits.push(CT.topUp(rev.top_up_req_no))
          if (rev.decreases.length > 0) bits.push(CT.lessNeeded)
          notify('success', bits.join(' '))
        }
        return { booking: res.data?.booking ?? null }
      }
      const res = await apiClient.createCateringBooking(input, saveKey)
      const created = res.data ?? null
      // The deposit is its own payment, recorded right after the booking
      // exists. One key for this form: a retry replays, never takes it twice.
      if (created && depositAmt != null) {
        try {
          const paid = await apiClient.addBookingPayment(created.id, { kind: 'deposit', method: depositMethod, amount: depositAmt }, depositKey)
          return { booking: paid.data ?? created }
        } catch {
          return { booking: created, depositFailed: true }
        }
      }
      return { booking: created }
    },
    onSuccess: ({ booking: b, depositFailed }) => {
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      if (depositFailed && depositAmt != null) notify('error', CXT.depositFailed(money(depositAmt)))
      else notify('success', !booking && depositAmt != null ? CXT.savedWithDeposit(money(depositAmt)) : CT.saved)
      onDone(b)
    },
    onError: (e, vars) => {
      const edits = kitchenEditsOf(e)
      if (edits) {
        setKitchenEdits({ edits, confirm: typeof vars === 'boolean' ? vars : vars.confirm })
        return
      }
      if (isBookingChanged(e)) setStale(true)
      notify('error', isBookingChanged(e) ? CBT.changedBySomeone : errMessage(e))
    },
  })
  const loadTheirs = useMutation({
    mutationFn: () => apiClient.getCateringBooking(booking?.id ?? ''),
    onSuccess: (r) => {
      if (!r.data) return
      const fresh = draftFromBooking(r.data)
      setD(fresh)
      setOfferFrom(fresh.guest_count)
      setBaseRevision(r.data.revision)
      setStale(false)
    },
    onError: (e) => notify('error', errMessage(e)),
  })

  const portions = d.lines.reduce((s, l) => s + l.qty, 0)

  return (
    <div className="space-y-5">
      <div>
        <Button variant="ghost" className="px-2" onClick={back}><ArrowLeft className="mr-2 h-4 w-4" /> {CT.back}</Button>
        <h2 className="mt-1 text-xl font-bold">{booking ? CXT.editTitle : CXT.newTitle}{booking ? ` — ${booking.booking_no}` : ''}</h2>
      </div>
      <ConfirmDialog open={askDiscard} title={CT.discardTitle} body={CT.discardBody} confirmLabel={CT.discard} cancelLabel={CT.keepEditing} destructive
        onConfirm={() => { setAskDiscard(false); onDone(null) }} onCancel={() => setAskDiscard(false)} />
      <ConfirmDialog open={kitchenEdits != null} title={CBT.kitchenEditTitle}
        body={kitchenEdits ? kitchenEditsText(kitchenEdits.edits) : undefined}
        confirmLabel={CBT.kitchenEditConfirm} cancelLabel={CT.keepEditing} busy={save.isPending}
        onConfirm={() => {
          const confirm = kitchenEdits?.confirm ?? false
          setKitchenEdits(null)
          save.mutate({ confirm, replaceKitchenEdits: true })
        }}
        onCancel={() => setKitchenEdits(null)}>
        <p className="text-sm text-muted-foreground">{CBT.kitchenEditHint}</p>
      </ConfirmDialog>
      <ConfirmDialog open={askSwitch && switchChange != null} title={PGT.switchTitle}
        body={switchChange ? PGT.switchBody(money(switchChange.from), money(switchChange.to)) : undefined}
        confirmLabel={PGT.switchConfirm} cancelLabel={CT.keepEditing} busy={save.isPending}
        onConfirm={() => { setAskSwitch(false); save.mutate(false) }} onCancel={() => setAskSwitch(false)} />

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-4">
          {/* 1 · Who and when */}
          <section className="space-y-3 rounded-lg border bg-card p-4">
            <h3 className="text-base font-semibold">{CXT.sectionWho}</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={CXT.customer}><Input maxLength={150} value={d.customer_name} onChange={(e) => set('customer_name', e.target.value)} /></Field>
              <Field label={CXT.phone}><Input inputMode="tel" maxLength={40} value={d.customer_phone} onChange={(e) => set('customer_phone', e.target.value)} /></Field>
              <Field label={CT.eventDate}><Input type="date" value={d.event_date} onChange={(e) => set('event_date', e.target.value)} /></Field>
              <Field label={CXT.guests}>
                <Input type="number" min={0} step={1} inputMode="numeric" value={d.guest_count}
                  onKeyDown={(e) => { if (['.', ',', 'e', 'E', '-'].includes(e.key)) e.preventDefault() }}
                  onFocus={() => setGuestsFocused(true)}
                  onBlur={() => {
                    setGuestsFocused(false)
                    // Nothing to offer: this count is now the one the dishes follow.
                    if (!guestsChangeOffer(d.lines, offerFrom, d.guest_count)) setOfferFrom(d.guest_count)
                  }}
                  onChange={(e) => set('guest_count', e.target.value)} />
              </Field>
            </div>
            <Disclosure open={showMore} onToggle={() => setShowMore((o) => !o)} title={CXT.moreDetails}>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={CXT.time}><Input type="time" value={d.event_time} onChange={(e) => set('event_time', e.target.value)} /></Field>
                <Field label={CXT.venue}><Input value={d.venue} onChange={(e) => set('venue', e.target.value)} /></Field>
                <Field label={CXT.eventName}><Input maxLength={150} placeholder={CT.eventNamePlaceholder} value={d.event_name} onChange={(e) => set('event_name', e.target.value)} /></Field>
                <div className="space-y-1.5 sm:col-span-2">
                  <span className="block text-sm font-medium">{CT.served}</span>
                  <Segmented label={CT.served} value={d.order_type} disabled={lockDishes} onChange={(v) => set('order_type', v)}
                    options={[{ value: 'dine_in', label: CT.dineIn }, { value: 'takeaway', label: CT.takeaway }]} />
                </div>
              </div>
            </Disclosure>
          </section>

          {/* 2 · Menu and price */}
          <section className="space-y-3 rounded-lg border bg-card p-4">
            <h3 className="text-base font-semibold">{CXT.sectionMenu}</h3>
            {showPricingSwitch(defaultPricing, d.pricing_mode, booking?.pricing_mode) && (
              <Segmented<PricingMode> label={PGT.pricing} value={d.pricing_mode} onChange={(v) => set('pricing_mode', v)}
                options={[{ value: 'per_guest', label: PGT.perHead }, { value: 'per_dish', label: PGT.perDish }]} />
            )}
            {perGuest && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                  <Field label={PGT.pricePerGuest}>
                    <Input inputMode="decimal" className="w-40" placeholder={money(0)} value={d.price_per_guest}
                      aria-invalid={(d.price_per_guest.trim() !== '' && parseAmount(d.price_per_guest) == null) || undefined}
                      onChange={(e) => set('price_per_guest', e.target.value)} />
                  </Field>
                  <span className="pb-2 text-sm font-medium tabular-nums">
                    {guestsOf(d.guest_count) ? PGT.timesGuests(guestsOf(d.guest_count) ?? 0, money(totals.subtotal)) : <span className="font-normal text-muted-foreground">{PGT.needGuestsFirst}</span>}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{PGT.dishesIncluded}</p>
              </div>
            )}
            {lockDishes ? (
              <p className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">{CT.dishesLocked}</p>
            ) : (
              <>
                <Field label={CXT.addDishes}><Input placeholder={CT.searchDishes} value={search} onChange={(e) => setSearch(e.target.value)} /></Field>
                <p className="text-sm text-muted-foreground">{perGuest ? PGT.addDishHint : CT.addDishHint}{guestsOf(d.guest_count) ? ` ${CXT.guestsDefaultHelp}` : ''}</p>
                <div className="grid max-h-[18rem] grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
                  {products.length === 0 && <p className="col-span-full text-sm text-muted-foreground">{CT.noDishes}</p>}
                  {products.map((p) => (
                    <button key={p.id} type="button"
                      onClick={() => set('lines', addLine(d.lines, { product_id: p.id, name: p.name, image_url: p.image_url, price: Number(p.price) || 0 }, defaultPortions(d.guest_count)))}
                      className="flex min-h-[4.5rem] items-center gap-2 rounded-lg border bg-card p-2 text-left hover:bg-muted touch-manipulation">
                      {p.image_url ? <img src={p.image_url} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" /> : <ItemAvatar name={p.name} />}
                      <span className="min-w-0">
                        <span className="line-clamp-2 text-sm font-medium">{p.name}</span>
                        <span className="text-xs text-muted-foreground">{money(Number(p.price) || 0)}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="space-y-2">
              <h4 className="text-sm font-semibold">{CXT.chosen(PT.dishCount(d.lines.length), PT.portions(portions))}</h4>
              {offer && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-950 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
                  <span className="flex-1">{CXT.guestsOfferHelp(guestsOf(offerFrom) ?? 0)}</span>
                  <Button size="sm" onClick={() => { set('lines', applyGuestsOffer(d.lines, offer)); setOfferFrom(d.guest_count); notify('success', CXT.guestsOfferDone) }}>
                    {CXT.guestsOffer(offer.productIds.length, offer.guests)}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setOfferFrom(d.guest_count)}>{CT.keep}</Button>
                </div>
              )}
              {d.lines.length === 0 ? (
                <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">{CXT.noneChosen}</p>
              ) : (
                <ul className="divide-y rounded-lg border">
                  {d.lines.map((l) => (
                    <li key={l.product_id} className="space-y-2 p-3">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        {l.image_url ? <img src={l.image_url} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <ItemAvatar name={l.name} />}
                        <span className="min-w-0 flex-[1_1_9rem]">
                          <span className="block text-sm font-medium">{l.name}</span>
                          {!perGuest && <span className="text-xs text-muted-foreground">{CT.menuPrice} {money(l.menu_price)} · {money(l.qty * unitPriceOf(l))}</span>}
                        </span>
                        {lockDishes ? (
                          <span className="text-sm font-semibold tabular-nums">{l.qty} {CXT.portionsUnit}</span>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <QtyStepper value={l.qty} unit="" label={l.name} min={0} integer onChange={(v) => set('lines', setLine(d.lines, l.product_id, { qty: v }))} />
                            <span className="text-sm text-muted-foreground">{CXT.portionsUnit}</span>
                            {matchesGuests(l, d.guest_count) && (
                              <span className="whitespace-nowrap rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100">{CXT.equalsGuests}</span>
                            )}
                          </div>
                        )}
                      </div>
                      {!lockDishes && !(l.qty > 0) && <p className="text-sm text-destructive">{CT.enterHowMany}</p>}
                      <div className="flex flex-wrap gap-2">
                        {!perGuest && (
                          <Input type="number" min={0} step="0.01" inputMode="decimal" className="w-36" aria-label={`${CT.specialPrice} — ${l.name}`}
                            placeholder={CT.specialPrice} value={l.special_price ?? ''}
                            onChange={(e) => set('lines', setLine(d.lines, l.product_id, { special_price: e.target.value === '' ? null : Math.max(0, Number(e.target.value) || 0) }))} />
                        )}
                        <Input className="min-w-[10rem] flex-1" maxLength={200} placeholder={CXT.lineNote} aria-label={`${CXT.lineNote} — ${l.name}`} value={l.note}
                          onChange={(e) => set('lines', setLine(d.lines, l.product_id, { note: e.target.value }))} />
                        {!lockDishes && (
                          <Button variant="ghost" size="icon" aria-label={`${CT.remove} ${l.name}`} onClick={() => set('lines', removeLine(d.lines, l.product_id))}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {hints && <p className="text-sm text-muted-foreground">{CT.portionHint(hints.guests, hints.lines)}</p>}
            </div>
            <Field label={CXT.customerNote}>
              <Textarea rows={2} value={d.notes} onChange={(e) => set('notes', e.target.value)} />
            </Field>
            <p className="text-xs text-muted-foreground">{CXT.customerNoteHelp}</p>
          </section>

          {/* 3 · Extras */}
          <section className="rounded-lg border bg-card p-4">
            <Disclosure open={showExtras} onToggle={() => setShowExtras((o) => !o)} title={CXT.sectionExtras}>
              <div className="space-y-3">
                {lockDishes ? (
                  (d.extras.length > 0 && <p className="text-sm text-muted-foreground">{d.extras.map((x) => `${x.name} × ${x.qty} ${x.unit}`).join(', ')}</p>) || null
                ) : (
                  <ExtrasPicker extras={d.extras} setExtras={(f) => setD((x) => ({ ...x, extras: f(x.extras) }))} />
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={CT.discount}><Input type="number" min={0} step="0.01" inputMode="decimal" value={d.discount_amount} onChange={(e) => set('discount_amount', e.target.value)} /></Field>
                  <Field label={CT.discountNote}><Input maxLength={200} value={d.discount_note} onChange={(e) => set('discount_note', e.target.value)} /></Field>
                </div>
              </div>
            </Disclosure>
          </section>
        </div>

        {/* Quote */}
        <aside className="space-y-3 rounded-lg border bg-card p-4 lg:sticky lg:top-4">
          <h3 className="text-base font-semibold">{CXT.quote}</h3>
          <div className="space-y-1.5 text-sm">
            {quote.rows.map((r) => (
              <div key={r.kind} className="flex justify-between gap-3 tabular-nums">
                <span className="text-muted-foreground">
                  {r.kind === 'perGuest' ? PGT.guestsTimes(r.guests, money(r.price)) : CXT.quoteDishes(PT.dishCount(r.dishes), PT.portions(r.portions))}
                </span>
                <span>{money(r.amount)}</span>
              </div>
            ))}
            {quote.included && <p className="text-muted-foreground">{PGT.included(PT.dishCount(quote.included.dishes), PT.portions(quote.included.portions))}</p>}
            {quote.extras.length > 0 && <p className="text-muted-foreground">{CXT.quoteExtras(quote.extras.length)}</p>}
            {totals.discount > 0 && (
              <div className="flex justify-between gap-3 tabular-nums"><span className="text-muted-foreground">{CT.discount}</span><span>−{money(totals.discount)}</span></div>
            )}
            <div className="flex justify-between gap-3 border-t pt-2 text-base font-bold tabular-nums"><span>{CT.total}</span><span>{money(totals.total)}</span></div>
            {booking && (
              <>
                <div className="flex justify-between gap-3 tabular-nums"><span className="text-muted-foreground">{CT.paid}</span><span>{money(totals.paid)}</span></div>
                <div className="flex justify-between gap-3 font-semibold tabular-nums"><span>{CXT.stillToPayLabel}</span><span>{money(totals.balance)}</span></div>
              </>
            )}
          </div>
          {!booking && (
            <div className="space-y-2">
              <Field label={CXT.depositNow}>
                <Input inputMode="decimal" placeholder={money(0)} aria-invalid={badDeposit || bigDeposit || undefined} value={deposit} onChange={(e) => setDeposit(e.target.value)} />
              </Field>
              {depositAmt != null && (
                <Segmented label={CXT.depositHow} value={depositMethod} onChange={setDepositMethod}
                  options={(['cash', 'bank_transfer', 'card'] as const).map((m) => ({ value: m, label: CT.methods[m] }))} />
              )}
              {badDeposit && <p className="text-sm text-destructive">{CT.amountInvalid}</p>}
              {bigDeposit && <p className="text-sm text-destructive">{CXT.depositTooBig}</p>}
            </div>
          )}
          <EquipmentClashNotice clashes={clashes} />
          {problem && (d.lines.length > 0 || problem !== 'needDish') && <p className="text-sm text-destructive">{CT[problem]}</p>}
          {stale && (
            <div role="alert" className="space-y-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm">
              <p>{CBT.changedBySomeone}</p>
              <Button variant="outline" size="sm" disabled={loadTheirs.isPending} onClick={() => loadTheirs.mutate()}>{CBT.loadTheirs}</Button>
            </div>
          )}
          <div className="flex flex-col gap-2">
            {booking ? (
              <Button disabled={blocked || save.isPending} onClick={() => (switchChange ? setAskSwitch(true) : save.mutate(false))}>{save.isPending ? CT.saving : CXT.saveChanges}</Button>
            ) : (
              <>
                <Button disabled={blocked || save.isPending} onClick={() => save.mutate(true)}>{save.isPending ? CT.saving : CXT.confirmBooking}</Button>
                <Button variant="outline" disabled={blocked || save.isPending} onClick={() => save.mutate(false)}>{CXT.saveEnquiry}</Button>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  )
}
